import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, ExternalLink, CheckCircle2, AlertTriangle, PenTool } from "lucide-react";

const label = (h) => `${h % 12 || 12}${h < 12 ? "am" : "pm"}`;

const HourBars = ({ hours }) => {
  const nowH = Number(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata", hour: "numeric", hour12: false })) % 24;
  return (
    <div className="flex items-end gap-1 h-28" data-testid="gemini-hour-chart">
      {hours.map((h) => {
        const rate = h.total ? h.ok / h.total : null;
        const color = rate === null ? "#1E293B" : rate >= 0.5 ? "#10B981" : rate >= 0.2 ? "#F59E0B" : "#F43F5E";
        return (
          <div key={h.hour} className="flex-1 h-full flex flex-col justify-end items-center gap-1 group" title={h.total ? `${label(h.hour)} IST: ${h.ok}/${h.total} succeeded` : `${label(h.hour)} IST: no data yet`}>
            <div className="w-full rounded-t-md transition-opacity group-hover:opacity-80" style={{ height: `${rate === null ? 6 : 12 + rate * 80}%`, background: color, outline: h.hour === nowH ? "2px solid #E2E8F0" : "none" }} />
            <span className="text-[9px] text-slate-600 h-3">{h.hour % 3 === 0 ? label(h.hour) : ""}</span>
          </div>
        );
      })}
    </div>
  );
};

export default function GeminiInsights() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/gemini/insights").then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!d) return null;
  const incidents = d.google_incidents || [];
  const last = d.last_attempt;
  return (
    <div className="grid lg:grid-cols-3 gap-4 mb-6" data-testid="gemini-insights">
      <div className="lg:col-span-2 bg-[#111726] border border-[#1E293B] rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-400" /><h3 className="font-bold font-display">When Gemini usually answers (IST)</h3></div>
          <div className="text-xs text-slate-400" data-testid="gemini-best-hours">
            {d.best_hours?.length ? <>Best times: <span className="text-emerald-300 font-semibold">{d.best_hours.map(label).join(", ")}</span></> : "Learning your best times — more data needed"}
          </div>
        </div>
        <HourBars hours={d.hours} />
        <div className="flex gap-4 text-[11px] text-slate-500 mt-3">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> usually works</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> sometimes busy</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-500" /> usually busy</span>
          <span>The queue tries more often in green hours.</span>
        </div>
      </div>
      <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5 space-y-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Google status</div>
          {incidents.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-300" data-testid="google-status-ok"><CheckCircle2 className="w-4 h-4" /> No reported Gemini outage</div>
          ) : incidents.map((i, k) => (
            <a key={k} href={i.url} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-sm text-amber-300 hover:underline" data-testid="google-incident"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {i.title}</a>
          ))}
          <a href={d.status_page} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 mt-2" data-testid="google-status-link">AI Studio status <ExternalLink className="w-3 h-3" /></a>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Last Gemini reply</div>
          <div className={`text-sm ${last?.ok ? "text-emerald-300" : "text-rose-300"}`} data-testid="gemini-last-attempt">
            {last ? `${last.ok ? "Worked" : "Busy"} · ${new Date(last.ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · ${last.model}` : "No calls yet"}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs" data-testid="miro-connection">
          <PenTool className="w-3.5 h-3.5 text-slate-500" />
          <span className={d.miro_connected ? "text-emerald-300" : "text-slate-500"}>{d.miro_connected ? "Miro connected — board content goes into notes" : "Miro not connected — add a token to read boards"}</span>
        </div>
      </div>
    </div>
  );
}
